import { Action, Reducer } from 'redux';
import { AppThunkAction } from '.';
import * as UnsubscribeService from '../services/unsubscribeService';

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface UnsubscribeState {
  email: string;
  appName: string;
  emailTypeName: string | null;
}

// -----------------
// ACTIONS -
// These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects;
// they just describe something that is going to happen.

interface RequestUnsubscribeAction {
  type: 'REQUEST_UNSUBSCRIBE';
  stateData: UnsubscribeState;
}
interface ReceiveUnsubscribeAction {
  type: 'RECEIVE_UNSUBSCRIBE';
}
interface RequestResubscribeAction {
  type: 'REQUEST_RESUBSCRIBE';
}
interface ReceiveResubscribeAction {
  type: 'RECEIVE_RESUBSCRIBE';
}

// Declare a 'discriminated union' type.
// This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | RequestUnsubscribeAction
  | ReceiveUnsubscribeAction
  | RequestResubscribeAction
  | ReceiveResubscribeAction;

// ----------------
// ACTION CREATORS -
// These are functions exposed to UI components that will trigger a state
// transition. They don't directly mutate state, but they can have
// external side-effects (such as loading data).

export const actionCreators = {
  unsubscribe:
    (unsubData: UnsubscribeState): AppThunkAction<KnownAction> =>
    (dispatch) => {
      UnsubscribeService.unsubscribe(unsubData).then(() => {
        dispatch({ type: 'RECEIVE_UNSUBSCRIBE' });
      });

      dispatch({ type: 'REQUEST_UNSUBSCRIBE', stateData: unsubData });
    },
  resubscribe: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
    const unsubData = getState().unsubscribe;
    UnsubscribeService.resubscribe(unsubData).then(() => {
      dispatch({ type: 'RECEIVE_RESUBSCRIBE' });
    });

    dispatch({ type: 'REQUEST_RESUBSCRIBE' });
  },
};

// ----------------
// REDUCER -
// For a given state and action, returns the new state.
// To support time travel, this must not mutate the old state.

const unloadedState: UnsubscribeState = {
  email: '',
  appName: '',
  emailTypeName: null,
};

export const reducer: Reducer<UnsubscribeState> = (
  state: UnsubscribeState | undefined,
  incomingAction: Action
): UnsubscribeState => {
  if (state === undefined) {
    return unloadedState;
  }

  const action = incomingAction as KnownAction;
  switch (action.type) {
    case 'REQUEST_UNSUBSCRIBE':
      return {
        ...action.stateData,
      };
    case 'REQUEST_RESUBSCRIBE':
    case 'RECEIVE_UNSUBSCRIBE':
    case 'RECEIVE_RESUBSCRIBE':
      return {
        ...state,
      };
    default:
      return state;
  }
};
